import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/api/client';
import { queryKeys } from '@/api/query-client';
import { broadcastLogout, useAuthStore } from '@/stores/auth-store';

/**
 * Profil courant, y compris les permissions dont dépend la garde `requirePlatformAdmin`.
 *
 * Contrairement à `frontend/`, cette app n'a pas de parcours d'inscription : seul le login
 * est câblé, réutilisant le même endpoint `/api/v1/auth/login` que l'app cliente.
 */
export function useCurrentUser() {
  const accessToken = useAuthStore((state) => state.accessToken);
  const setUser = useAuthStore((state) => state.setUser);

  return useQuery({
    queryKey: queryKeys.currentUser,

    queryFn: async () => {
      const { data } = await api.GET('/api/v1/auth/me');
      setUser(data ?? null);

      return data ?? null;
    },

    // Pas de jeton, pas de requête : évite un 401 systématique au premier rendu.
    enabled: accessToken !== null,
    staleTime: 5 * 60_000,
  });
}

/**
 * Sur un compte 2FA (obligatoire pour PlatformAdmin, voir AuthorizationBehavior côté API),
 * l'API ne renvoie aucun jeton : seulement un jeton de défi que l'appelant présente à
 * `useVerifyTwoFactorLogin` avec le code TOTP. `setSession` n'est donc invoqué QUE si les
 * jetons sont bien présents dans la réponse — même idiome que `frontend/`.
 */
export function useLogin() {
  const setSession = useAuthStore((state) => state.setSession);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (credentials: { email: string; password: string }) => {
      const { data } = await api.POST('/api/v1/auth/login', { body: credentials });

      return data!;
    },

    onSuccess: async (result) => {
      if (result.tokens === null || result.tokens === undefined) {
        return;
      }

      setSession(result.tokens.accessToken);

      // Le profil est rechargé immédiatement : c'est lui qui porte la permission
      // `platform:admin` dont dépend l'accès à tout le reste de l'application.
      await queryClient.invalidateQueries({ queryKey: queryKeys.currentUser });
    },
  });
}

/** Second temps de la connexion sur un compte 2FA : jeton de défi + code TOTP (ou de récupération). */
export function useVerifyTwoFactorLogin() {
  const setSession = useAuthStore((state) => state.setSession);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: { challengeToken: string; code: string }) => {
      const { data } = await api.POST('/api/v1/auth/login/2fa', { body: payload });

      return data!;
    },

    onSuccess: async (tokens) => {
      setSession(tokens.accessToken);
      await queryClient.invalidateQueries({ queryKey: queryKeys.currentUser });
    },
  });
}

/** Démarre l'enrôlement 2FA : renvoie le secret et l'URI otpauth:// à afficher en QR code. */
export function useSetupTwoFactor() {
  return useMutation({
    mutationFn: async () => {
      const { data } = await api.POST('/api/v1/auth/2fa/setup', {});

      return data!;
    },
  });
}

/**
 * Demande l'envoi du code de vérification par email, préalable à l'activation de la 2FA :
 * il prouve l'accès à la boîte mail avant que l'application d'authentification ne devienne
 * le second facteur du compte.
 */
export function useRequestTwoFactorEmailCode() {
  return useMutation({
    mutationFn: async () => {
      await api.POST('/api/v1/auth/2fa/email/request');
    },
  });
}

/** Confirme l'enrôlement avec le code email et le premier code TOTP, renvoie les codes de récupération. */
export function useEnableTwoFactor() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: { emailCode: string; code: string }) => {
      const { data } = await api.POST('/api/v1/auth/2fa/enable', { body: payload });

      return data!;
    },

    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.currentUser });
    },
  });
}

/**
 * Désactive la 2FA. Exige le mot de passe du compte ET un code du second facteur (TOTP ou
 * code de récupération) : le mot de passe seul ne prouve pas la possession de l'application
 * d'authentification.
 *
 * Pour un compte PlatformAdmin, désactiver la 2FA reverrouille immédiatement l'accès à tout
 * le reste du back-office (voir `AuthorizationBehavior` côté API) — c'est la conséquence
 * attendue de l'obligation, pas un cas particulier à gérer ici.
 */
export function useDisableTwoFactor() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: { password: string; code: string }) => {
      await api.POST('/api/v1/auth/2fa/disable', { body: payload });
    },

    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.currentUser });
    },
  });
}

export function useLogout() {
  const clear = useAuthStore((state) => state.clear);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      await api.POST('/api/v1/auth/logout', { body: { allSessions: false } });
    },

    // `onSettled` et non `onSuccess` : même si l'appel réseau échoue, l'utilisateur a
    // demandé à se déconnecter — le laisser sur une interface admin authentifiée serait
    // à la fois déroutant et risqué sur un poste partagé.
    onSettled: () => {
      clear();
      queryClient.clear();
      broadcastLogout();
    },
  });
}
